import config, { apiUrl } from '../../config';
import QueryString from 'query-string';
import {
  Box,
  Card,
  Container,
  Typography,
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  TextField,
  InputAdornment,
  Stack,
  Paper,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import FitnessCenterOutlinedIcon from '@mui/icons-material/FitnessCenterOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import SearchIcon from '@mui/icons-material/Search';
import SortIcon from '@mui/icons-material/Sort';
import React, { useEffect, useState } from 'react';
import ServerError from '../error/ServerError';
import Loading from '../../components/shared/Loading';
import { useAuth } from '../../hooks/use-auth.js';
import { useNavigate } from 'react-router-dom';
import UserResults from './UserResults';
import InviteUserDialog from './InviteUserDialog';
import { useQuery } from '@apollo/client/react';
import Page from '../Page';
import { GET_USERS } from '../../graphql/users';
import { GET_TRAINING_PLAN_LIST } from '../../graphql/training_plan';
import { useSnackbar } from 'notistack';

const LIMIT = 50;
const SUBSCRIPTION_FILTERS = {
  ALL: 'ALL',
  PREMIUM: 'PREMIUM',
  PRO: 'PRO',
};
const CLIENT_STATUS_FILTERS = {
  ALL: 'ALL',
  NEEDS_ATTENTION: 'NEEDS_ATTENTION',
  INACTIVE: 'INACTIVE',
  NEW: 'NEW',
  ENGAGED: 'ENGAGED',
  HIGH_PERFORMERS: 'HIGH_PERFORMERS',
};

function getDaysSinceLastWorkout(user) {
  if (!user.lastWorkoutSessionAt) {
    return null;
  }

  return Math.max(
    0,
    Math.floor(
      (Date.now() - new Date(user.lastWorkoutSessionAt).getTime()) / 86400000
    )
  );
}

function getDaysSinceSignup(user) {
  if (!user.createdAt) {
    return 0;
  }

  return Math.max(
    0,
    Math.floor((Date.now() - new Date(user.createdAt).getTime()) / 86400000)
  );
}

function getPrimaryTrainingPlanSession(user) {
  return (
    (user.trainingPlanSessions || []).find(
      (session) => session.status === 'ACTIVE'
    ) ||
    user.trainingPlanSessions?.[0] ||
    null
  );
}

function getAdherence(user) {
  const session = getPrimaryTrainingPlanSession(user);
  const eventCount = session?.trainingPlan?.eventCount || 0;
  if (!eventCount) {
    return null;
  }

  return Math.min(
    100,
    Math.round(((session?.completedEvents?.length || 0) / eventCount) * 100)
  );
}

function userMatchesClientStatus(user, filter) {
  const daysSinceLastWorkout = getDaysSinceLastWorkout(user);
  const adherence = getAdherence(user);

  if (filter === CLIENT_STATUS_FILTERS.NEEDS_ATTENTION) {
    return (
      adherence == null ||
      adherence < 60 ||
      daysSinceLastWorkout == null ||
      daysSinceLastWorkout > 7
    );
  }
  if (filter === CLIENT_STATUS_FILTERS.INACTIVE) {
    return daysSinceLastWorkout == null || daysSinceLastWorkout > 7;
  }
  if (filter === CLIENT_STATUS_FILTERS.NEW) {
    return getDaysSinceSignup(user) <= 7;
  }
  if (filter === CLIENT_STATUS_FILTERS.ENGAGED) {
    return (
      adherence != null &&
      adherence >= 60 &&
      daysSinceLastWorkout != null &&
      daysSinceLastWorkout <= 7
    );
  }
  if (filter === CLIENT_STATUS_FILTERS.HIGH_PERFORMERS) {
    return adherence != null && adherence >= 80;
  }

  return true;
}

function userHasTrainingPlan(user, trainingPlanId) {
  return (user.trainingPlanSessions || []).some(
    (session) =>
      session.trainingPlan?._id === trainingPlanId ||
      session.trainingPlanId === trainingPlanId
  );
}

function getTrainingPlanEnrollmentCount(users, trainingPlanId) {
  return users.filter((user) => userHasTrainingPlan(user, trainingPlanId))
    .length;
}

export default function UserList() {
  const [showInviteUserDialog, setShowInviteUserDialog] = useState(false);
  const [isAddingTestUser, setIsAddingTestUser] = useState(false);
  const [subscriptionFilter, setSubscriptionFilter] = useState(
    SUBSCRIPTION_FILTERS.ALL
  );
  const [clientStatusFilter, setClientStatusFilter] = useState(
    CLIENT_STATUS_FILTERS.ALL
  );
  const [programFilter, setProgramFilter] = useState('ALL');
  const [lastActiveFilter, setLastActiveFilter] = useState('ANY');
  const [term, setTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');
  const [liveUserDetails, setLiveUserDetails] = useState({
    error: null,
    loading: false,
    loadedUserIds: null,
    byUserId: {},
  });
  const queryParams = QueryString.parse(window.location.search);
  const trainerId = queryParams.trainerId || null;
  const subscriptionTier =
    subscriptionFilter === SUBSCRIPTION_FILTERS.ALL
      ? null
      : subscriptionFilter.toLowerCase();
  const normalizedTerm = debouncedTerm.trim().toLowerCase();
  const searchTerm = normalizedTerm || null;

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedTerm(term);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [term]);

  const { loading, error, data, previousData, fetchMore, refetch } = useQuery(
    GET_USERS,
    {
      variables: {
        input: {
          limit: LIMIT,
          trainerId,
          subscriptionTier,
          searchTerm,
        },
        trainerId, // for the total user count query
        subscriptionTier,
        searchTerm,
      },
    }
  );
  const {
    loading: trainingPlansLoading,
    error: trainingPlansError,
    data: trainingPlansData,
  } = useQuery(GET_TRAINING_PLAN_LIST, {
    variables: { trainerId },
  });
  const navigate = useNavigate();
  const auth = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const queryData = data || previousData;
  const rawUsers = queryData?.users || [];
  const users = rawUsers.map((user) => {
    const liveDetail = liveUserDetails.byUserId[user._id];

    if (!liveDetail) {
      return user;
    }

    return {
      ...user,
      trainingPlanSessions:
        liveDetail.trainingPlanSessions || user.trainingPlanSessions || [],
    };
  });
  const userIds = rawUsers.map((user) => user._id).join(',');
  const trainingPlans = (
    trainingPlansData?.trainingPlanList?.trainingPlans || []
  )
    .slice()
    .sort(
      (a, b) =>
        getTrainingPlanEnrollmentCount(users, b._id) -
        getTrainingPlanEnrollmentCount(users, a._id)
    );

  useEffect(() => {
    if (!auth.user || rawUsers.length === 0) {
      setLiveUserDetails({
        error: null,
        loading: false,
        loadedUserIds: rawUsers.length === 0 ? userIds : null,
        byUserId: {},
      });
      return;
    }

    let isCurrent = true;

    async function loadLiveUserDetails() {
      setLiveUserDetails((current) => ({
        ...current,
        error: null,
        loading: true,
      }));

      try {
        const authToken = await auth.user.getIdToken();
        const headers = new Headers({
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        });
        const detailResponses = await Promise.all(
          rawUsers.map(async (user) => {
            const response = await fetch(
              `${apiUrl}/api/admin/users/${user._id}`,
              {
                method: 'GET',
                headers,
              }
            );

            if (!response.ok) {
              throw new Error('Failed to load live user program details.');
            }

            const json = await response.json();
            return {
              userId: user._id,
              trainingPlanSessions: json.trainingPlanSessions || [],
            };
          })
        );

        if (!isCurrent) {
          return;
        }

        setLiveUserDetails({
          error: null,
          loading: false,
          loadedUserIds: userIds,
          byUserId: detailResponses.reduce((result, detail) => {
            result[detail.userId] = {
              trainingPlanSessions: detail.trainingPlanSessions,
            };
            return result;
          }, {}),
        });
      } catch (err) {
        if (!isCurrent) {
          return;
        }

        setLiveUserDetails((current) => ({
          ...current,
          error: err.message,
          loading: false,
        }));
      }
    }

    loadLiveUserDetails();

    return () => {
      isCurrent = false;
    };
  }, [auth.user, userIds]);

  const handleSearch = async (event) => {
    event.preventDefault();
    const authToken = await auth.user.getIdToken();

    return fetch(`${apiUrl}/api/admin/users/search?email=${term}`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      }),
    })
      .then((res) => res.json())
      .then((response) => {
        if (!response.user) {
          window.alert('No user found.');
          return;
        }

        navigate(`/trainer/users/${response.user._id}`);
      });
  };
  const handleLoadMore = async (event) => {
    event.preventDefault();

    await fetchMore({
      variables: {
        input: {
          limit: LIMIT,
          lastId: users[users.length - 1]._id,
          trainerId,
          subscriptionTier,
          searchTerm,
        },
        trainerId,
        subscriptionTier,
        searchTerm,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        return {
          ...previousResult,
          users: [...previousResult.users, ...fetchMoreResult.users],
        };
      },
    });
  };

  const handleAddTestUser = async (event) => {
    event.preventDefault();

    if (!auth.user || isAddingTestUser) {
      return;
    }

    setIsAddingTestUser(true);

    try {
      const authToken = await auth.user.getIdToken();
      const headers = new Headers({
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      });
      const meResponse = await fetch(`${apiUrl}/v1/users/me`, {
        method: 'GET',
        headers,
      });

      if (!meResponse.ok) {
        throw new Error('Failed to load current user.');
      }

      const meJson = await meResponse.json();
      const loggedInUser = meJson.user;

      if (!loggedInUser?._id) {
        throw new Error('Current user is missing an ID.');
      }

      const timestamp = Date.now();
      const createResponse = await fetch(`${apiUrl}/v1/accounts`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name: 'Test User',
          email: `test-user-${timestamp}@tempokit.com`,
          password: `test-user-${timestamp}`,
          fromTrainerId: loggedInUser._id,
        }),
      });

      if (!createResponse.ok) {
        const message = await createResponse.text();
        throw new Error(message || 'Failed to add test user.');
      }

      enqueueSnackbar('Test user added', {
        variant: 'success',
        autoHideDuration: 1000,
      });
      await refetch();
    } catch (err) {
      enqueueSnackbar(`Error: ${err.message}`, {
        variant: 'error',
      });
    } finally {
      setIsAddingTestUser(false);
    }
  };

  const handleExportToCsv = async (event) => {
    event.preventDefault();
    const authToken = await auth.user.getIdToken();

    return fetch(`${apiUrl}/v1/users/export`, {
      method: 'POST',
      headers: new Headers({
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({ body: 'CSV', includeSubscriptionTier: true }),
    })
      .then((res) => res.blob())
      .then((blob) => {
        const today = new Date().toISOString().split('T')[0];
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `tempo-users-${today}.csv`;
        document.body.appendChild(a); // we need to append the element to the dom -> otherwise it will not work in firefox
        a.click();
        a.remove(); //afterwards we remove the element again
      })
      .catch((err) => {
        window.alert(err);
      });
  };

  if (loading && !queryData) {
    return <Loading />;
  }
  if (error) return <ServerError message="Failed to load" />;
  const liveEnrollmentDataReady =
    rawUsers.length === 0 ||
    (liveUserDetails.loadedUserIds === userIds &&
      !liveUserDetails.loading &&
      !liveUserDetails.error);
  const filteredUsers = searchTerm
    ? users
    : users.filter((user) => {
        const matchesStatus = userMatchesClientStatus(user, clientStatusFilter);
        const matchesProgram =
          programFilter === 'ALL' || userHasTrainingPlan(user, programFilter);
        const matchesLastActive =
          lastActiveFilter === 'ANY' ||
          (lastActiveFilter === '7_DAYS' && getDaysSinceSignup(user) <= 7) ||
          (lastActiveFilter === '30_DAYS' && getDaysSinceSignup(user) <= 30);

        return matchesStatus && matchesProgram && matchesLastActive;
      });
  const subscriptionFilterLabel =
    subscriptionFilter === SUBSCRIPTION_FILTERS.PRO ? 'Pro' : 'Premium';
  const clientMetrics = queryData?.trainer?.clientMetrics;
  const userCount = clientMetrics?.total ?? users.length;
  const activeCount = clientMetrics?.active ?? 0;
  const inactiveCount = clientMetrics?.inactive ?? 0;
  const newCount = clientMetrics?.new ?? 0;
  const sidebarItems = [
    {
      label: 'All Clients',
      value: CLIENT_STATUS_FILTERS.ALL,
      count: userCount,
      icon: GroupsOutlinedIcon,
      color: '#5b4bff',
    },
    {
      label: 'Inactive (7+ days)',
      value: CLIENT_STATUS_FILTERS.INACTIVE,
      count: inactiveCount,
      icon: SortIcon,
      color: '#ef4444',
    },
    {
      label: 'New Clients',
      value: CLIENT_STATUS_FILTERS.NEW,
      count: newCount,
      icon: PersonAddAltOutlinedIcon,
      color: '#16a34a',
    },
  ];
  const metricCards = [
    {
      label: 'Active Clients',
      value: userCount,
      detail: `${activeCount} active in last 7 days`,
      color: '#5b4bff',
      bg: '#edeaff',
      icon: GroupsOutlinedIcon,
    },
    {
      label: 'New This Week',
      value: newCount,
      detail: 'View new clients',
      color: '#2388ff',
      bg: '#e8f2ff',
      icon: CalendarTodayOutlinedIcon,
    },
  ];

  return (
    <Page title={`Users : ${config.appDisplayName}`}>
      <InviteUserDialog
        open={showInviteUserDialog}
        onClose={() => {
          setShowInviteUserDialog(false);
        }}
      />
      <Container
        maxWidth={false}
        sx={{ bgcolor: '#f8fafc', minHeight: '100%', py: 3 }}
      >
        <Box
          alignItems={{ xs: 'flex-start', md: 'center' }}
          display="flex"
          justifyContent="space-between"
          mb={3}
          gap={2}
          flexDirection={{ xs: 'column', md: 'row' }}
        >
          <Box>
            <Typography color="#0f172a" fontWeight={800} variant="h3">
              Clients
            </Typography>
            <Typography
              color="text.secondary"
              sx={{ mt: 0.75 }}
              variant="body1"
            >
              Manage your clients, track engagement, and take action.
            </Typography>
          </Box>
          <Stack direction="row" flexWrap="wrap" gap={1.5}>
            <Button
              startIcon={<AddIcon />}
              sx={{
                bgcolor: '#5b4bff',
                boxShadow: '0 8px 18px rgba(91, 75, 255, 0.2)',
                fontWeight: 700,
                px: 2,
                '&:hover': { bgcolor: '#493ae8' },
              }}
              variant="contained"
              onClick={async (e) => {
                e.preventDefault();
                setShowInviteUserDialog(true);
              }}
            >
              Invite New Client
            </Button>
            <Button
              disabled={isAddingTestUser}
              startIcon={<PersonAddAltOutlinedIcon />}
              sx={{
                bgcolor: '#fff',
                borderColor: '#dfe5ef',
                color: '#17213f',
                fontWeight: 700,
                px: 2,
              }}
              variant="outlined"
              onClick={handleAddTestUser}
            >
              {isAddingTestUser ? 'Adding...' : 'Add Test Client'}
            </Button>
          </Stack>
        </Box>

        <Box
          display="grid"
          gap={2}
          gridTemplateColumns={{
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(5, minmax(0, 1fr))',
          }}
          mb={3}
        >
          {metricCards.map((metric) => {
            const MetricIcon = metric.icon;
            return (
              <Card
                key={metric.label}
                elevation={0}
                sx={{
                  alignItems: 'center',
                  border: '1px solid #e4e8f0',
                  borderRadius: 2,
                  display: 'flex',
                  gap: 2,
                  minHeight: 104,
                  p: 2,
                }}
              >
                <Box
                  sx={{
                    alignItems: 'center',
                    bgcolor: metric.bg,
                    borderRadius: '50%',
                    color: metric.color,
                    display: 'flex',
                    height: 56,
                    justifyContent: 'center',
                    width: 56,
                  }}
                >
                  <MetricIcon />
                </Box>
                <Box>
                  <Typography color="#0f172a" fontWeight={800} variant="h4">
                    {metric.value}
                  </Typography>
                  <Typography color="#17213f" variant="body2">
                    {metric.label}
                  </Typography>
                  <Typography
                    color={metric.color}
                    fontWeight={650}
                    variant="caption"
                  >
                    {metric.detail}
                  </Typography>
                </Box>
              </Card>
            );
          })}
        </Box>

        <Paper
          elevation={0}
          sx={{
            border: '1px solid #e4e8f0',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Box
            alignItems="center"
            display="flex"
            flexWrap="wrap"
            gap={2}
            justifyContent="space-between"
            p={2}
          >
            <Box alignItems="center" display="flex" flexWrap="wrap" gap={2}>
              <Box component="form" onSubmit={handleSearch}>
                <TextField
                  name="email"
                  onChange={(event) => setTerm(event.target.value)}
                  placeholder="Search clients..."
                  size="small"
                  sx={{
                    bgcolor: '#fff',
                    minWidth: { xs: '100%', sm: 280 },
                    '& .MuiOutlinedInput-root': { borderRadius: 1.5 },
                  }}
                  type="search"
                  value={term}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <Select
                  IconComponent={KeyboardArrowDownIcon}
                  value={programFilter}
                  onChange={(event) => setProgramFilter(event.target.value)}
                >
                  <MenuItem value="ALL">Program: All</MenuItem>
                  {trainingPlans.map((plan) => (
                    <MenuItem key={plan._id} value={plan._id}>
                      Program: {plan.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <Select
                  IconComponent={KeyboardArrowDownIcon}
                  value={lastActiveFilter}
                  onChange={(event) => setLastActiveFilter(event.target.value)}
                >
                  <MenuItem value="ANY">Last Active: Any</MenuItem>
                  <MenuItem value="7_DAYS">Last Active: 7 days</MenuItem>
                  <MenuItem value="30_DAYS">Last Active: 30 days</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <Select
                  IconComponent={KeyboardArrowDownIcon}
                  value={subscriptionFilter}
                  onChange={(event) =>
                    setSubscriptionFilter(event.target.value)
                  }
                >
                  <MenuItem value={SUBSCRIPTION_FILTERS.ALL}>
                    Subscription: All
                  </MenuItem>
                  <MenuItem value={SUBSCRIPTION_FILTERS.PREMIUM}>
                    Subscription: Premium
                  </MenuItem>
                  <MenuItem value={SUBSCRIPTION_FILTERS.PRO}>
                    Subscription: Pro
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Stack direction="row" gap={1.5}>
              <Button
                startIcon={<FilterListIcon />}
                sx={{
                  borderColor: '#dfe5ef',
                  color: '#17213f',
                  fontWeight: 700,
                }}
                variant="outlined"
                onClick={handleExportToCsv}
              >
                Export CSV
              </Button>
              <Button
                startIcon={<SortIcon />}
                sx={{
                  borderColor: '#dfe5ef',
                  color: '#17213f',
                  fontWeight: 700,
                }}
                variant="outlined"
              >
                Sort: Last Active
              </Button>
            </Stack>
          </Box>

          <Divider />

          <Box
            display="grid"
            gridTemplateColumns={{ xs: '1fr', lg: '280px 1fr' }}
          >
            <Box
              sx={{
                borderRight: { lg: '1px solid #e4e8f0' },
                p: 2,
              }}
            >
              <Stack spacing={0.5}>
                {sidebarItems.map((item) => {
                  const ItemIcon = item.icon;
                  const selected = clientStatusFilter === item.value;
                  return (
                    <Button
                      key={item.value}
                      fullWidth
                      onClick={() => setClientStatusFilter(item.value)}
                      sx={{
                        bgcolor: selected ? '#f0edff' : 'transparent',
                        borderRadius: 1.5,
                        color: selected ? '#5b4bff' : '#31415f',
                        justifyContent: 'space-between',
                        px: 1.25,
                        py: 1,
                        textTransform: 'none',
                        '&:hover': { bgcolor: '#f5f7fb' },
                      }}
                    >
                      <Box alignItems="center" display="flex" gap={1.25}>
                        <ItemIcon sx={{ color: item.color, fontSize: 18 }} />
                        <Typography
                          fontWeight={selected ? 800 : 600}
                          variant="body2"
                        >
                          {item.label}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          bgcolor: selected ? '#fff' : 'transparent',
                          borderRadius: 999,
                          color: selected ? '#5b4bff' : '#64748b',
                          fontWeight: 800,
                          px: selected ? 1 : 0,
                        }}
                        variant="caption"
                      >
                        {item.count}
                      </Typography>
                    </Button>
                  );
                })}
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Typography
                color="#475569"
                fontWeight={700}
                mb={1.5}
                variant="body2"
              >
                Programs
              </Typography>
              <Stack spacing={0.5}>
                {trainingPlansLoading && (
                  <Typography color="text.secondary" variant="body2">
                    Loading programs...
                  </Typography>
                )}
                {trainingPlansError && (
                  <Typography color="error" variant="body2">
                    Failed to load programs.
                  </Typography>
                )}
                {!trainingPlansLoading &&
                  !trainingPlansError &&
                  trainingPlans.slice(0, 8).map((plan) => {
                    return (
                      <Button
                        key={plan._id}
                        fullWidth
                        onClick={() => setProgramFilter(plan._id)}
                        sx={{
                          bgcolor:
                            programFilter === plan._id
                              ? '#f0edff'
                              : 'transparent',
                          borderRadius: 1.5,
                          color:
                            programFilter === plan._id ? '#5b4bff' : '#31415f',
                          justifyContent: 'space-between',
                          px: 1.25,
                          py: 1,
                          textTransform: 'none',
                          '&:hover': { bgcolor: '#f5f7fb' },
                        }}
                      >
                        <Box alignItems="center" display="flex" gap={1.25}>
                          <FitnessCenterOutlinedIcon
                            sx={{ color: '#5b4bff', fontSize: 18 }}
                          />
                          <Typography fontWeight={600} noWrap variant="body2">
                            {plan.title}
                          </Typography>
                        </Box>
                        <Typography
                          color="#64748b"
                          fontWeight={700}
                          variant="caption"
                        >
                          {getTrainingPlanEnrollmentCount(users, plan._id)}
                        </Typography>
                      </Button>
                    );
                  })}
                <Button
                  onClick={() => setProgramFilter('ALL')}
                  sx={{
                    color: '#5b4bff',
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                  }}
                >
                  View all programs
                </Button>
              </Stack>
            </Box>

            <Box sx={{ p: 2 }}>
              {!liveEnrollmentDataReady ? (
                <Box
                  aria-busy={!liveUserDetails.error}
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    minHeight: 280,
                  }}
                >
                  {liveUserDetails.error ? (
                    <Typography color="error" variant="body2">
                      {liveUserDetails.error}
                    </Typography>
                  ) : (
                    <>
                      <CircularProgress size={32} />
                      <Typography
                        color="text.secondary"
                        sx={{ mt: 2 }}
                        variant="body2"
                      >
                        Loading live program enrollment data...
                      </Typography>
                    </>
                  )}
                </Box>
              ) : (
                <>
                  <Typography color="text.secondary" mb={2} variant="body2">
                    Showing 1 to {filteredUsers.length} of{' '}
                    {userCount || users.length} clients
                  </Typography>
                  {subscriptionFilter !== SUBSCRIPTION_FILTERS.ALL && (
                    <Typography
                      color="textSecondary"
                      display="block"
                      sx={{ mb: 1 }}
                      variant="caption"
                    >
                      Showing {filteredUsers.length} loaded users with active{' '}
                      {subscriptionFilterLabel} subscriptions.
                    </Typography>
                  )}

                  <UserResults users={filteredUsers} />

                  {users.length > 1 && users.length >= LIMIT && (
                    <Box mt={3}>
                      <Button variant="outlined" onClick={handleLoadMore}>
                        LOAD MORE
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Box>
        </Paper>
      </Container>
    </Page>
  );
}
